import React, { ComponentProps, ReactElement } from 'react';

import PropTypes from 'prop-types';

import { useDeprecate } from '@adsk/alloy-react-helpers';
import {
  AlertWarningFilledIcon,
  CheckmarkCircleFilledIcon,
  RenderIconType,
} from '@adsk/alloy-react-icon';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

import { Variant, FILE_HANDLER_VARIANTS, variantPropType } from './consts';
import Metadata from './layout/Metadata';
import Name from './layout/Name';

const defaultRenderFileName = (props: ComponentProps<typeof Name>) => (
  <Name {...props} />
);
const defaultRenderMetadata = (props: ComponentProps<typeof Metadata>) => (
  <Metadata {...props} />
);

const defaultRenderTooltipTarget = (
  props: ComponentProps<typeof Tooltip.Target>,
) => <Tooltip.Target {...props} shouldWrapChildren={false} />;

type FileHandlerProps = StylableComponent<HTMLDivElement> & {
  name: string;
  /** @deprecated use `alertMessage` instead */
  error?: string;
  /** Message to display in error or warning tooltip */
  alertMessage?: string;
  metadata?: string;
  variant?: Variant;
  renderIcon?: RenderIconType;
  renderFileName?: (props: ComponentProps<typeof Name>) => ReactElement | null;
  renderMetadata?: (
    props: ComponentProps<typeof Metadata>,
  ) => ReactElement | null;
  renderMainAction?: () => ReactElement | null;
  renderSecondaryAction?: () => ReactElement | null;
};

/**
 * A FileHandler
 * docs.
 */
const FileHandler = React.forwardRef<HTMLDivElement, FileHandlerProps>(
  (
    {
      className,
      style,
      name,
      error,
      alertMessage,
      metadata,
      variant = FILE_HANDLER_VARIANTS.DEFAULT,
      children,
      renderIcon,
      renderFileName = defaultRenderFileName,
      renderMetadata = defaultRenderMetadata,
      renderMainAction,
      renderSecondaryAction,
      ...rest
    },
    ref,
  ) => {
    useDeprecate(!!error, 'FileHandler', 'error', 'alertMessage');

    const theme = useTheme();
    const tokens = theme.tokens?.component?.fileHandler;
    const containerTokens = tokens?.container;
    const iconTokens = tokens?.icon;
    const actionTokens = tokens?.action;
    const statusIconTokens = tokens?.['status-icon'];

    const finalVariant = variant || FILE_HANDLER_VARIANTS.DEFAULT;
    const disabled = [
      FILE_HANDLER_VARIANTS.LOADING,
      FILE_HANDLER_VARIANTS.REMOVED,
    ].includes(finalVariant);
    const iconStateTokens = iconTokens?.disabled?.[disabled ? 'true' : 'false'];

    const showAlert = [
      FILE_HANDLER_VARIANTS.ERROR,
      FILE_HANDLER_VARIANTS.WARNING,
    ].includes(finalVariant);

    return (
      <div
        {...rest}
        ref={ref}
        className={className}
        css={[containerTokens, style]}
      >
        {renderIcon?.({
          style: {
            marginRight: iconTokens?.['margin-right'],
            flexShrink: iconTokens?.['flex-shrink'],
            opacity: iconStateTokens?.opacity,
          },
          color: iconStateTokens?.['icon-color'],
        })}
        <div css={[tokens?.['content-container']]}>
          {renderFileName({ disabled, variant: finalVariant, children: name })}
          {renderMetadata({ disabled, children: metadata })}
        </div>
        {[
          FILE_HANDLER_VARIANTS.DEFAULT,
          FILE_HANDLER_VARIANTS.ERROR,
          FILE_HANDLER_VARIANTS.WARNING,
        ].includes(finalVariant) &&
          renderMainAction && (
            <div css={[actionTokens?.base, actionTokens?.main]}>
              {renderMainAction()}
            </div>
          )}
        {[
          FILE_HANDLER_VARIANTS.LOADING,
          FILE_HANDLER_VARIANTS.SUCCESS,
          FILE_HANDLER_VARIANTS.ERROR,
          FILE_HANDLER_VARIANTS.WARNING,
        ].includes(finalVariant) && (
          <div css={[actionTokens?.base, actionTokens?.status]}>
            {finalVariant === FILE_HANDLER_VARIANTS.LOADING && (
              <ProgressRing
                size={ProgressRing.SIZES.XSMALL}
                data-testid="FileHandlerLoader"
              />
            )}
            {finalVariant === FILE_HANDLER_VARIANTS.SUCCESS && (
              <CheckmarkCircleFilledIcon
                color={statusIconTokens?.success?.['icon-color']}
              />
            )}
            {showAlert && (
              <Tooltip
                content={alertMessage || error}
                renderTarget={defaultRenderTooltipTarget}
              >
                <AlertWarningFilledIcon
                  color={
                    finalVariant === FILE_HANDLER_VARIANTS.ERROR
                      ? statusIconTokens?.error?.['icon-color']
                      : statusIconTokens?.warning?.['icon-color']
                  }
                />
              </Tooltip>
            )}
          </div>
        )}
        {renderSecondaryAction && (
          <div css={[actionTokens?.base, actionTokens?.secondary]}>
            {renderSecondaryAction()}
          </div>
        )}
      </div>
    );
  },
);

FileHandler.displayName = 'FileHandler';

FileHandler.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Content */
  children: PropTypes.any,
  /** The style variant */
  variant: variantPropType,
  /** File name */
  name: PropTypes.string.isRequired,
  /** Message to display in error or warning tooltip */
  alertMessage: PropTypes.string,
  /** File name */
  metadata: PropTypes.string,
  /** Icon type */
  renderIcon: PropTypes.func,
  /** Render custom name component */
  renderFileName: PropTypes.func,
  /** Render main action */
  renderMainAction: PropTypes.func,
  /** Render secondary action */
  renderSecondaryAction: PropTypes.func,
};

export default Object.assign(FileHandler, {
  VARIANTS: FILE_HANDLER_VARIANTS,
});
